import {IAddon, IOffer, IOffers, PRICE_VARIABLE} from '@dazn/lp-web/src/types';
import {isMultiPromoDiscountsEnabled} from '@dazn/lp-web/src/contexts/LandingPageContext';
import {getOfferChargeTier} from '@dazn/lp-web/src/components/ContentTiers/contentTiersUtils';
import React from 'react';
import localisePrice from '../../utils/localisePrice';

interface IGetCountdownDiscount {
    offers: IOffers | null;
    promoOffers: IOffers | null;
    multiplePromoOffers: IOffers[] | null;
    data: any;
}

interface IGetPrice {
    price: number | undefined;
    isDicountAvailable: boolean;
    discountPercentage: number | undefined;
    discountedPrice: number | undefined;
}

function getPriceStrings(priceText: string, variable: string) {
    const prefixEnd = priceText?.indexOf(variable);
    const suffixStart = prefixEnd + variable.length;
    const prefix = priceText?.substring(0, prefixEnd);
    const suffix = priceText?.substring(suffixStart);

    return {prefix, suffix};
}

function getCountdownOffer(
    offers: IOffers | null,
    promoOffers: IOffers | null,
    countdownData: any,
    multiplePromoOffers: IOffers[] | null
): IOffer | IAddon | undefined {
    function finder(offer: any) {
        if (countdownData?.billingPeriod) {
            return (
                offer?.EntitlementSetId === countdownData?.entitlementSetId &&
                offer?.BillingPeriod === countdownData?.billingPeriod
            );
        }

        return offer?.EntitlementSetId === countdownData?.entitlementSetId;
    }

    function filterDiscountedOffers(data: any[]) {
        if (data.length === 1) {
            return data[0];
        } else {
            const discountedOffer = data.find((obj) =>
                obj.Offers.some((offer: any) =>
                    offer.ChargeTiers.some(
                        (tier: any) => tier.Discount !== null
                    )
                )
            );
            return discountedOffer ? discountedOffer : data[0];
        }
    }

    const filteredData = multiplePromoOffers
        ? multiplePromoOffers
              ?.map((obj: IOffers) => {
                  const filteredOffers =
                      obj && obj.Offers && obj.Offers.length > 0
                          ? obj.Offers.filter(finder)
                          : [];
                  return {
                      ...obj,
                      Offers: filteredOffers
                  };
              })
              .filter((obj) => obj.Offers.length > 0)
        : [];

    const finalPromoOffer =
        isMultiPromoDiscountsEnabled && filteredData
            ? filterDiscountedOffers(filteredData)
            : promoOffers;

    if (
        (isMultiPromoDiscountsEnabled && filteredData) ||
        (promoOffers?.Offers && promoOffers.Offers.length > 0)
    ) {
        const offer =
            finalPromoOffer?.Offers?.find(finder) ||
            finalPromoOffer?.Addons?.find(finder);
        if (offer) return offer;
    }

    return offers?.Offers?.find(finder) || offers?.Addons?.find(finder);
}

function getPrice(offer: any): IGetPrice | undefined {
    if (offer && offer.ChargeTiers && offer.ChargeTiers.length > 0) {
        const chargeTier = getOfferChargeTier(offer);
        const isDicountAvailable = chargeTier?.Discount !== null;

        return {
            price: chargeTier?.Price,
            isDicountAvailable: isDicountAvailable,
            discountPercentage: chargeTier?.Discount?.Percentage,
            discountedPrice: chargeTier?.Discount?.Price
        };
    }
}

export function getFinalPrice(priceText: string, price: any) {
    if (priceText) {
        if (priceText?.includes(PRICE_VARIABLE) && price) {
            const {prefix, suffix} = getPriceStrings(priceText, PRICE_VARIABLE);
            const localPrice = localisePrice(price);

            return (
                <>
                    {prefix}
                    <span>{localPrice}</span>
                    {suffix}
                </>
            );
        }

        return priceText;
    }
}

export function getCountdownDiscount({
    offers,
    data,
    promoOffers,
    multiplePromoOffers
}: IGetCountdownDiscount) {
    const offer: any = getCountdownOffer(
        offers,
        promoOffers,
        data,
        multiplePromoOffers
    );
    const priceData = getPrice(offer);
    const priceText = data?.price;
    if (priceData) {
        const {price, isDicountAvailable, discountPercentage, discountedPrice} =
            priceData;
        const finalPrice = isDicountAvailable
            ? getFinalPrice(priceText, discountedPrice)
            : getFinalPrice(priceText, price);

        return {
            isDicountAvailable: isDicountAvailable,
            price: finalPrice,
            actualPrice: price,
            discountPercentage: discountPercentage
                ? Math.round(discountPercentage)
                : discountPercentage,
            discountedPrice: discountedPrice
        };
    } else {
        return {
            isDicountAvailable: false,
            price: priceText,
            actualPrice: priceText
        };
    }
}
